import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, Typography } from '@mui/material';

interface PrivateRouteProps {
  children: React.ReactElement;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { user, unifiedUser, currentShopId, availableShops, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // ユーザーはログインしているが、店舗アクセス権がない場合
  // unifiedUserがnullの場合も含める（Firestoreにユーザードキュメントがない場合）
  if (!unifiedUser || availableShops.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          p: 3,
        }}
      >
        <Typography variant="h5" gutterBottom>
          アクセス権限がありません
        </Typography>
        <Typography variant="body1" color="text.secondary">
          店舗へのアクセス権限が付与されていません。
          <br />
          オーナーアカウントでログインしてください。
        </Typography>
      </Box>
    );
  }

  // 複数店舗がある場合で、店舗選択画面以外にアクセスしようとした場合
  if (
    availableShops.length > 1 &&
    !currentShopId &&
    location.pathname !== '/select-shop'
  ) {
    return <Navigate to="/select-shop" replace />;
  }

  // 店舗が選択されていない場合（1店舗のみの場合は自動選択されるはず）
  if (!currentShopId && location.pathname !== '/select-shop') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return children;
};

export default PrivateRoute;
